import type { SelfDescribingJson } from '@snowplow/browser-tracker';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';

interface ExperimentViewEventData {
  experimentId?: string;
  experimentName: string;
  variantName: string;
  variantIndex?: number;
  targetUrl?: string;
  description?: string;
  splitSource?:
    | 'absmartly'
    | 'amplitude'
    | 'marketing-tool'
    | 'unbounce'
    | 'builder';
  trigger?: string;
}

/**
 * Track Experiment view event in Snowplow.
 * v.0.23.0;
 * @public
 *
 * @param eventData - Object that contains event properties described in the Experiment View Event document
 * (see https://healthcareinc.atlassian.net/wiki/spaces/ENGINEERING/pages/2718826518/Experiment+View#Schema-Fields).
 * Note: Package version 0.23.0 now uses version 2-0-0 of the experiment view.
 *
 * @example
 * ```ts
 *
 * import {trackExperimentView} from '@hc-frontend/third-party-snowplow';
 *
 * trackExperimentView({
 *     experimentId: '365',
 *     experimentName: 'STORE_EXPERIMENT_NAME_U65',
 *     variantIndex: 0,
 *     variantName: 'VARIANT_NAME',
 *     description: 'Additional description',
 *     splitSource: 'absmartly',
 *     trigger: 'inactivity',
 * });
 * ```
 */
function trackExperimentView(
  eventData: ExperimentViewEventData,
  contexts?: Array<SelfDescribingJson> | null,
): void {
  const {
    experimentId,
    experimentName,
    variantIndex,
    variantName,
    targetUrl,
    description,
    splitSource,
    trigger,
  } = eventData;
  trackSelfDescribingEvent({
    event: {
      schema: 'iglu:com.healthcare/experiment_view/jsonschema/2-1-1',
      data: {
        experiment_id: experimentId,
        experiment_name: experimentName,
        variant_index: variantIndex,
        variant_name: variantName,
        target_url: targetUrl,
        description,
        split_source: splitSource,
        trigger,
      },
    },
    context: contexts,
  });
}

export type { ExperimentViewEventData };
export { trackExperimentView };
